import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { mapMedicineInventoryRow, mapMedicineMovement, mapMedicineReportFarm, unitFields } from '@/types';
import type {
    MedicineBulkCount,
    MedicineBulkCountApiResponse,
    MedicineBulkCountPayload,
    MedicineBulkPurchase,
    MedicineBulkPurchaseApiResponse,
    MedicineBulkPurchasePayload,
    MedicineBulkRowError,
    MedicineCountDatesFarm,
    MedicineCountSheetApiFarm,
    MedicineCountSheetFarm,
    MedicineInventoryApiRow,
    MedicineInventoryKey,
    MedicineInventoryKeyApiRow,
    MedicineInventoryRow,
    MedicineInventoryScope,
    MedicineMovementsApiPage,
    MedicineMovementsParams,
    MedicineMovementsResult,
    MedicineReportApiFarm,
    MedicineReportFarm,
    MedicineReportRequestFarm,
    MedicineTreatmentWeek,
    MedicineTreatmentWeeksParams,
} from '@/types';
import ApiRequest from '@/plugins/store/actions/api/ApiRequest';

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
};

/** The period and farm scope every endpoint below takes, as its query string. */
function scopeQuery(scope: MedicineInventoryScope): string {
    return `${scope.period}&location_ids=${scope.locationIds.join(',')}`;
}

/** The Log's drug and diagnosis filters as query parameters; none when a filter is empty. */
function drugsAndDiagnosesQuery(drugs: string[], diagnoses: string[]): string[] {
    const parts: string[] = [];

    if (drugs.length) {
        parts.push(`drugs=${drugs.join(',')}`);
    }
    if (diagnoses.length) {
        parts.push(`diagnosis=${diagnoses.map(encodeURIComponent).join(',')}`);
    }

    return parts;
}

/** The movements endpoint's query string: scope, optional key and type filters, sort and the requested page. */
export function movementsQuery(params: MedicineMovementsParams): string {
    const parts = [scopeQuery(params.scope)];

    if (params.drugId !== undefined && params.unitId !== undefined) {
        parts.push(`drug_id=${params.drugId}`, `unit_id=${params.unitId}`);

        if (params.allHistory) {
            parts.push('all_history=true');
        }
    } else {
        parts.push(...drugsAndDiagnosesQuery(params.drugs ?? [], []));
    }
    if (params.types.length) {
        parts.push(`type=${params.types.join(',')}`);
    }
    parts.push(...drugsAndDiagnosesQuery([], params.diagnoses));
    if (params.search?.trim()) {
        parts.push(`search=${encodeURIComponent(params.search.trim())}`);
    }

    parts.push(`sort=${params.sort}`, `dir=${params.descending ? 'desc' : 'asc'}`, `page_number=${params.page}`, `page_size=${params.pageSize}`);

    return parts.join('&');
}

export const queryKeys = {
    getInventoryRoot: ['inventory'],
    inventoryListRoot: ['inventory', 'list'],
    inventoryListWithParams: (scope: Ref<MedicineInventoryScope>) => [...queryKeys.inventoryListRoot, scope],
    inventoryDetailRoot: ['inventory', 'detail'],
    inventoryDetailWithParams: (scope: Ref<MedicineInventoryScope>, farmId: Ref<number>, drugId: Ref<number>, unitId: Ref<number>) => [...queryKeys.inventoryDetailRoot, scope, farmId, drugId, unitId],
    inventoryMovementsRoot: ['inventory', 'movements'],
    inventoryMovementsWithParams: (params: Ref<MedicineMovementsParams>) => [...queryKeys.inventoryMovementsRoot, params],
    treatmentWeeksWithParams: (params: Ref<MedicineTreatmentWeeksParams>) => [...queryKeys.getInventoryRoot, 'treatments-by-week', params],
    keysWithFarms: (locationIds: Ref<number[]>) => [...queryKeys.getInventoryRoot, 'keys', locationIds],
    diagnosesWithFarms: (locationIds: Ref<number[]>) => [...queryKeys.getInventoryRoot, 'diagnoses', locationIds],
    bulkCountWithParams: (farmId: Ref<number | null>, date: Ref<string>) => [...queryKeys.getInventoryRoot, 'bulk-count', farmId, date],
    bulkPurchaseWithParams: (farmId: Ref<number | null>, date: Ref<string>) => [...queryKeys.getInventoryRoot, 'bulk-purchase', farmId, date],
    countSheetWithFarms: (locationIds: Ref<number[]>) => [...queryKeys.getInventoryRoot, 'count-sheet', locationIds],
    countDatesWithFarms: (locationIds: Ref<number[]>) => [...queryKeys.getInventoryRoot, 'count-dates', locationIds],
};

/**
 * Every ledger key with a purchase, count or treatment consumption, reconciled over the period and
 * farm scope. Unpaged.
 */
export function useMedicineInventory(scope: Ref<MedicineInventoryScope>, options: UseQueryOptions<MedicineInventoryRow[]> = {}) {
    return useQuery<MedicineInventoryRow[]>({
        queryKey: queryKeys.inventoryListWithParams(scope),
        queryFn: () =>
            new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true })
                .send('get', `health/inventory?${scopeQuery(scope.value)}`)
                .then((rows: MedicineInventoryApiRow[]) => rows.map(mapMedicineInventoryRow)),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

/** One ledger key's metrics, computed by the same query as its list row. */
export function useMedicineInventoryDetail(scope: Ref<MedicineInventoryScope>, farmId: Ref<number>, drugId: Ref<number>, unitId: Ref<number>, options: UseQueryOptions<MedicineInventoryRow> = {}) {
    return useQuery<MedicineInventoryRow>({
        queryKey: queryKeys.inventoryDetailWithParams(scope, farmId, drugId, unitId),
        queryFn: () =>
            new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true })
                .send('get', `health/inventory/${farmId.value}/${drugId.value}/${unitId.value}?${scopeQuery(scope.value)}`)
                .then((row: MedicineInventoryApiRow) => mapMedicineInventoryRow(row)),
        // A key outside the user's farms is a 404, an answer rather than a hiccup: show it at once instead of retrying.
        retry: (failureCount, error) => (error as { status?: number }).status !== 404 && failureCount < 3,
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

/**
 * One page of the period's movements. Filtering, sorting and paging are all the server's: the
 * previous page stays on screen while the next loads, so changing a filter never blanks the table.
 */
export function useMedicineMovements(params: Ref<MedicineMovementsParams>, options: UseQueryOptions<MedicineMovementsResult> = {}) {
    return useQuery<MedicineMovementsResult>({
        queryKey: queryKeys.inventoryMovementsWithParams(params),
        queryFn: () =>
            new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true }).send('get', `health/inventory/movements?${movementsQuery(params.value)}`).then((page: MedicineMovementsApiPage) => ({
                rows: page.data.map(mapMedicineMovement),
                totalRows: page.total_rows ?? page.data.length,
                totalPages: page.total_pages ?? 1,
                netQuantity: page.net_quantity ?? null,
                diagnosisTotals:
                    page.totals_by_diagnosis?.map((total) => ({
                        diagnosis: total.diagnosis,
                        ...unitFields(total),
                        quantity: total.quantity,
                        treatments: total.treatments,
                        drug_names: total.drug_names,
                    })) ?? null,
            })),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

/** Every farm, drug and unit of the farms in scope that has an instruction or any entry, without ledger figures: what the Log's farm and drug pickers offer. */
export function useMedicineInventoryKeys(locationIds: Ref<number[]>, options: UseQueryOptions<MedicineInventoryKey[]> = {}) {
    return useQuery<MedicineInventoryKey[]>({
        queryKey: queryKeys.keysWithFarms(locationIds),
        queryFn: () =>
            new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true })
                .send('get', `health/inventory/keys?location_ids=${locationIds.value.join(',')}`)
                .then((rows: MedicineInventoryKeyApiRow[]) => rows.map((row) => ({ farm_id: row.farm_id, farm_name: row.farm_name, drug_id: row.drug_id, drug_name: row.drug_name, ...unitFields(row) }))),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

/** Treatments and retreatments per drug and week over the Log's filters: what its chart draws. */
export function useMedicineTreatmentWeeks(params: Ref<MedicineTreatmentWeeksParams>, options: UseQueryOptions<MedicineTreatmentWeek[]> = {}) {
    return useQuery<MedicineTreatmentWeek[]>({
        queryKey: queryKeys.treatmentWeeksWithParams(params),
        queryFn: () =>
            new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true }).send(
                'get',
                [`health/inventory/treatments-by-week?${scopeQuery(params.value.scope)}`, ...drugsAndDiagnosesQuery(params.value.drugs, params.value.diagnoses)].join('&')
            ),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

/** The distinct diagnoses of the instructions on the farms: what the Log's diagnosis filter offers. */
export function useMedicineDiagnoses(locationIds: Ref<number[]>, options: UseQueryOptions<string[]> = {}) {
    return useQuery<string[]>({
        queryKey: queryKeys.diagnosesWithFarms(locationIds),
        queryFn: () => new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true }).send('get', `health/inventory/diagnoses?location_ids=${locationIds.value.join(',')}`),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

/** The keys a bulk count of one farm covers on a date, with the balance each is expected to show. The server recomputes every figure on save. */
export function useMedicineBulkCount(farmId: Ref<number | null>, date: Ref<string>, options: UseQueryOptions<MedicineBulkCount> = {}) {
    return useQuery<MedicineBulkCount>({
        queryKey: queryKeys.bulkCountWithParams(farmId, date),
        queryFn: () =>
            new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true })
                .send('get', `health/inventory/bulk-count?farm_id=${farmId.value}&date=${date.value}`)
                .then((response: MedicineBulkCountApiResponse) => ({
                    farmId: response.farm_id,
                    date: response.date,
                    rows: response.rows.map((row) => ({ drug_id: row.drug_id, drug_name: row.drug_name, ...unitFields(row), calculated: row.calculated })),
                    reasons: response.reasons,
                })),
        // Read fresh on every opening and never cached across farms or days, so the expected balances are today's.
        // A refetch while the form is open (a reconnect, a save elsewhere) is harmless: useBulkEntryDialog keeps what was typed.
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        ...options,
    });
}

/** A bulk save the server refused, with the rows it could not accept. */
export class BulkSaveRejectedError extends Error {
    constructor(readonly rowErrors: MedicineBulkRowError[]) {
        super('Bulk save rejected');
    }
}

/** Posts a bulk count or purchase; a 400 that names rows throws `BulkSaveRejectedError`. */
async function postBulk(path: string, payload: MedicineBulkCountPayload | MedicineBulkPurchasePayload): Promise<{ saved: number }> {
    try {
        return await new ApiRequest({ skipHandleError: true }).send('post', path, payload);
    } catch (error) {
        // With skipHandleError the client rethrows the HTTP response itself.
        const rowErrors = (error as { data?: { errors?: MedicineBulkRowError[] } }).data?.errors;

        if (Array.isArray(rowErrors)) {
            throw new BulkSaveRejectedError(rowErrors);
        }

        throw error;
    }
}

/** Saves the counted rows of a bulk count. */
export function useSaveMedicineBulkCount() {
    const queryClient = useQueryClient();

    return useMutation<{ saved: number }, unknown, MedicineBulkCountPayload>({
        mutationFn: (payload) => postBulk('health/inventory/bulk-count', payload),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.getInventoryRoot }),
    });
}

/** The keys a purchase for one farm offers, each with what it was last bought as on or before the date. */
export function useMedicineBulkPurchase(farmId: Ref<number | null>, date: Ref<string>, options: UseQueryOptions<MedicineBulkPurchase> = {}) {
    return useQuery<MedicineBulkPurchase>({
        queryKey: queryKeys.bulkPurchaseWithParams(farmId, date),
        queryFn: () =>
            new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true })
                .send('get', `health/inventory/bulk-purchase?farm_id=${farmId.value}&date=${date.value}`)
                .then((response: MedicineBulkPurchaseApiResponse) => ({
                    farmId: response.farm_id,
                    date: response.date,
                    rows: response.rows.map((row) => ({ drug_id: row.drug_id, drug_name: row.drug_name, ...unitFields(row), last_purchase: row.last_purchase })),
                })),
        // As the bulk count: read fresh on every opening; a refetch while open keeps what was typed.
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        ...options,
    });
}

/** Saves the ticked rows of a bulk purchase. */
export function useSaveMedicineBulkPurchase() {
    const queryClient = useQueryClient();

    return useMutation<{ saved: number }, unknown, MedicineBulkPurchasePayload>({
        mutationFn: (payload) => postBulk('health/inventory/bulk-purchase', payload),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.getInventoryRoot }),
    });
}

/** The count sheet of every farm in the scope; only fetched while the print dialog is open. */
export function useMedicineCountSheet(locationIds: Ref<number[]>, options: UseQueryOptions<MedicineCountSheetFarm[]> = {}) {
    return useQuery<MedicineCountSheetFarm[]>({
        queryKey: queryKeys.countSheetWithFarms(locationIds),
        queryFn: () =>
            new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true })
                .send('get', `health/inventory/count-sheet?location_ids=${locationIds.value.join(',')}`)
                .then((farms: MedicineCountSheetApiFarm[]) =>
                    farms.map((farm) => ({ ...farm, rows: farm.rows.map((row) => ({ drug_id: row.drug_id, drug_name: row.drug_name, ...unitFields(row), calculated: row.calculated })) }))
                ),
        // A sheet shows today's balances, so it is read fresh every time the dialog opens.
        staleTime: 0,
        gcTime: 0,
        ...options,
    });
}

/** The days each farm has a count on: what the report's second step picks two of. */
export function useMedicineCountDates(locationIds: Ref<number[]>, options: UseQueryOptions<MedicineCountDatesFarm[]> = {}) {
    return useQuery<MedicineCountDatesFarm[]>({
        queryKey: queryKeys.countDatesWithFarms(locationIds),
        queryFn: () => new ApiRequest({ skipHandleError: true, skipLoadingIndicator: true }).send('get', `health/inventory/count-dates?location_ids=${locationIds.value.join(',')}`),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

/**
 * The two-count comparison report. A mutation, like the farm-to-farm report: it runs when the user
 * asks for it and is never refetched behind their back.
 */
export function useMedicineReport() {
    return useMutation<MedicineReportFarm[], unknown, MedicineReportRequestFarm[]>({
        mutationFn: async (farms) => {
            const response: MedicineReportApiFarm[] | undefined = await new ApiRequest({ skipHandleError: true }).send('post', 'health/inventory/report', { farms });

            if (!Array.isArray(response)) {
                throw new Error('Invalid inventory report response');
            }

            return response.map(mapMedicineReportFarm);
        },
    });
}
